import { useState, useCallback, useEffect, useRef, useMemo, useContext } from 'react';
import ActiveTabContext from '@/context/ActiveTabContext';

// Service function type - can accept multiple params (spread)
type ServiceFn<TData, TParams extends unknown[]> = (...params: TParams) => Promise<TData>;

interface UseRequestOptions<TData, TParams extends unknown[]> {
  manual?: boolean;
  defaultParams?: TParams;
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
  refreshDeps?: unknown[];
  ready?: boolean;
  debounceInterval?: number;
  // formatResult is used by some umi code to transform data
  formatResult?: (data: unknown) => TData;
  // Initial data before first fetch
  initialData?: TData;
  // Cache options (for compatibility, not fully implemented)
  cacheKey?: string;
  staleTime?: number;
  /**
   * If true, refetch data when tab becomes active (default: false)
   * Set to false (default) to preserve form state when switching tabs
   */
  refetchOnTabActive?: boolean;
}

// Cache entry type
interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
}

// Simple in-memory cache - global singleton
const requestCache = new Map<string, CacheEntry>();

// Track in-flight requests to prevent duplicate concurrent calls
const inFlightRequests = new Map<string, Promise<unknown>>();

/** Wrapped response structure from API */
interface WrappedResponse<T = unknown> {
  data: T;
  total?: number;
  success?: boolean;
}

/** Extended array with metadata from wrapped response */
type ExtendedArray<T> = T[] & {
  __response?: WrappedResponse<T[]>;
  total?: number;
  success?: boolean;
  data?: T[];
};

/**
 * Helper to extract data from wrapped responses
 * Handles both:
 * - { data: [...], success, total } -> returns the array with metadata
 * - { data: singleObject } -> returns singleObject directly
 * 
 * This matches umi's useRequest behavior where services that wrap data
 * in { data: ... } have the inner data extracted automatically.
 */
function extractData<T>(result: T): unknown {
  // Check if this looks like a wrapped response: { data: ... }
  // Only unwrap if `data` is the main/only meaningful property
  if (
    result &&
    typeof result === 'object' &&
    !Array.isArray(result) &&
    'data' in result
  ) {
    const wrappedResult = result as WrappedResponse;
    const innerData = wrappedResult.data;

    // If inner data is null/undefined, return it directly to avoid
    // exposing the wrapper object ({ data: null, response }) to consumers
    if (innerData == null) {
      return innerData;
    }
    
    // If data is an array, extract it with metadata
    if (Array.isArray(innerData)) {
      const arr = innerData as ExtendedArray<unknown>;
      // Attach metadata for components that need it
      arr.__response = wrappedResult as WrappedResponse<unknown[]>;
      arr.total = wrappedResult.total;
      arr.success = wrappedResult.success;
      // Allow `{ data }` destructuring to still work
      arr.data = arr;
      return arr;
    }
    
    // If data is an object (single item response like user details),
    // return the inner data directly
    if (typeof innerData === 'object') {
      return innerData;
    }
  }
  return result;
}

/**
 * Drop-in replacement for umi's useRequest
 * Uses React state for more compatibility with umi's behavior
 * 
 * @example
 * // Single parameter (backward compatible)
 * const { run } = useRequest((id) => fetchUser(id), { manual: true });
 * run(userId);
 * 
 * // Multiple parameters
 * const { run } = useRequest((id, status) => updateStatus(id, status), { manual: true });
 * run(userId, 'active');
 */
export function useRequest<TData = unknown, TParams extends unknown[] = unknown[]>(
  service: ServiceFn<TData, TParams>,
  options: UseRequestOptions<TData, TParams> = {}
) {
  const {
    manual = false,
    defaultParams,
    onSuccess,
    onError,
    refreshDeps = [],
    ready = true,
    debounceInterval = 0,
    formatResult,
    initialData,
    cacheKey,
    staleTime = 0,
    refetchOnTabActive = false
  } = options;

  // Initialize with initialData or cached data
  const getInitialData = (): TData | undefined => {
    if (cacheKey && requestCache.has(cacheKey)) {
      const cached = requestCache.get(cacheKey)!;
      if (staleTime === 0 || Date.now() - cached.timestamp < staleTime) {
        return cached.data as TData;
      }
    }
    return initialData;
  };

  const [data, setData] = useState<TData | undefined>(getInitialData);
  const [loading, setLoading] = useState(!manual && !getInitialData());
  const [error, setError] = useState<Error | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  
  // Check if we're in an active tab
  const activeTabContext = useContext(ActiveTabContext);
  const isInActiveTab = activeTabContext?.isActive ?? true;
  
  // Stable reference to refreshDeps
  const refreshDepsKey = useMemo(() => JSON.stringify(refreshDeps), [refreshDeps]);

  // Use refs for all callback dependencies to keep run() stable
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const formatResultRef = useRef(formatResult);
  formatResultRef.current = formatResult;
  const debounceIntervalRef = useRef(debounceInterval);
  debounceIntervalRef.current = debounceInterval;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;
  const staleTimeRef = useRef(staleTime);
  staleTimeRef.current = staleTime;

  // Run function that can be called manually - STABLE reference (no deps that change)
  // Supports multiple parameters: run(param1, param2, ...)
  const run = useCallback(async (...params: TParams) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    const executeRequest = async (): Promise<TData> => {
      // Generate a cache key that includes params for deduplication
      const fullCacheKey = cacheKeyRef.current 
        ? `${cacheKeyRef.current}:${JSON.stringify(params)}` 
        : null;
      
      // Check cache first (with staleTime consideration)
      if (fullCacheKey && requestCache.has(fullCacheKey)) {
        const cached = requestCache.get(fullCacheKey)!;
        const staleTimeMs = staleTimeRef.current || 0;
        if (staleTimeMs === 0 || Date.now() - cached.timestamp < staleTimeMs) {
          // Return cached data without making a request
          const cachedData = cached.data as TData;
          if (mountedRef.current) {
            setData(cachedData);
            setLoading(false);
          }
          return cachedData;
        }
      }
      
      // Check if there's already an in-flight request for this key
      if (fullCacheKey && inFlightRequests.has(fullCacheKey)) {
        try {
          const result = await inFlightRequests.get(fullCacheKey) as TData;
          if (mountedRef.current) {
            setData(result);
            setLoading(false);
          }
          return result;
        } catch {
          // If the in-flight request failed, proceed with our own request
        }
      }
      
      setLoading(true);
      setError(null);
      
      const requestPromise = (async (): Promise<TData> => {
        try {
          let result: TData = await serviceRef.current(...params);
          
          // Apply formatResult if provided
          if (formatResultRef.current) {
            result = formatResultRef.current(result as unknown);
          }
          
          // Auto-extract data from wrapped responses (arrays or single objects)
          const finalData = extractData(result) as TData;
          
          // Cache the result if cacheKey is provided
          if (fullCacheKey) {
            requestCache.set(fullCacheKey, { data: finalData, timestamp: Date.now() });
          }
          
          if (mountedRef.current) {
            setData(finalData);
            onSuccessRef.current?.(finalData);
          }
          return finalData;
        } catch (err) {
          if (mountedRef.current) {
            setError(err as Error);
            onErrorRef.current?.(err as Error);
          }
          throw err;
        } finally {
          if (mountedRef.current) {
            setLoading(false);
          }
          // Remove from in-flight tracking
          if (fullCacheKey) {
            inFlightRequests.delete(fullCacheKey);
          }
        }
      })();
      
      // Track the in-flight request
      if (fullCacheKey) {
        inFlightRequests.set(fullCacheKey, requestPromise);
      }
      
      return requestPromise;
    };

    if (debounceIntervalRef.current > 0) {
      return new Promise<TData>((resolve, reject) => {
        debounceTimer.current = setTimeout(async () => {
          try {
            const result = await executeRequest();
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }, debounceIntervalRef.current);
      });
    }

    return executeRequest();
  }, []); // Empty deps = stable reference

  // Store defaultParams in a ref to avoid stale closures
  const defaultParamsRef = useRef(defaultParams);
  defaultParamsRef.current = defaultParams;

  // Helper to get default params with proper type casting (DRY)
  // Ensure we always return an array, even if defaultParams was incorrectly provided as a non-array
  const getDefaultParams = useCallback((): TParams => {
    const params = defaultParamsRef.current;
    // If params is undefined or null, return empty array
    if (params == null) {
      return [] as unknown as TParams;
    }
    // If params is already an array, return it
    if (Array.isArray(params)) {
      return params as unknown as TParams;
    }
    // If params is not an array (incorrect usage), wrap it in an array
    // This provides a safety net for incorrect usage
    return [params] as unknown as TParams;
  }, []);

  // Track state for tab-aware fetching
  const hasFetchedRef = useRef(false);
  const prevIsActiveRef = useRef(isInActiveTab);
  const prevRefreshDepsKeyRef = useRef(refreshDepsKey);
  const isInitialMountRef = useRef(true);
  
  // Auto-run on mount if not manual
  useEffect(() => {
    mountedRef.current = true;
    const runDefaultParams = () => {
      run(...getDefaultParams()).catch(() => {
        // Error state and optional onError callbacks are set inside run().
      });
    };
    
    const wasInactive = !prevIsActiveRef.current;
    const isNowActive = isInActiveTab;
    const tabJustBecameActive = wasInactive && isNowActive;
    const refreshDepsChanged = prevRefreshDepsKeyRef.current !== refreshDepsKey;
    const isInitialMount = isInitialMountRef.current;
    
    // Update refs for next render
    prevIsActiveRef.current = isInActiveTab;
    prevRefreshDepsKeyRef.current = refreshDepsKey;
    isInitialMountRef.current = false;
    
    // Skip if not ready or in manual mode
    if (manual || !ready) {
      return;
    }
    
    // Case 1: Initial mount - fetch if in active tab
    if (isInitialMount) {
      if (isInActiveTab) {
        hasFetchedRef.current = true;
        runDefaultParams();
      }
      return;
    }
    
    // Case 2: Tab just became active (was inactive, now active)
    if (tabJustBecameActive && hasFetchedRef.current) {
      // Only refetch if explicitly requested
      if (refetchOnTabActive) {
        runDefaultParams();
      }
      // Otherwise: preserve existing data, don't refetch
      return;
    }
    
    // Case 3: refreshDeps changed (e.g., different ID parameter)
    // This is a legitimate reason to refetch, but only if in active tab
    if (refreshDepsChanged && isInActiveTab && hasFetchedRef.current) {
      runDefaultParams();
      return;
    }
    
    // Case 4: First fetch for this component (e.g., tab became active for first time)
    if (!hasFetchedRef.current && isInActiveTab) {
      hasFetchedRef.current = true;
      runDefaultParams();
    }
    
    return () => {
      mountedRef.current = false;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual, ready, refreshDepsKey, isInActiveTab, refetchOnTabActive]);

  // Refresh function - also stable since run is now stable
  const refresh = useCallback(() => {
    return run(...getDefaultParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    loading,
    error,
    run,
    refresh,
    mutate: setData
  };
}

export default useRequest;
